// Sentry shims — previously imported from './sentry' which has been removed.
// logger still calls these, so provide no-op fallbacks.
const addBreadcrumb = (..._args: unknown[]) => {
  /* no-op */
};
const captureMessage = (..._args: unknown[]) => {
  /* no-op */
};
const sentryCaptureException = (..._args: unknown[]) => {
  /* no-op */
};

/**
 * Log levels in order of severity
 *
 * @remarks
 * Levels are hierarchical - setting minLevel to WARN will log WARN, ERROR, and FATAL,
 * but not DEBUG or INFO. Use DEBUG in development, INFO+ in production.
 */
export enum LogLevel {
  /** Detailed debugging information (development only) */
  DEBUG = 'debug',
  /** General informational messages */
  INFO = 'info',
  /** Warning messages for potentially harmful situations */
  WARN = 'warn',
  /** Error events that might still allow the application to continue */
  ERROR = 'error',
  /** Severe errors that may lead to application termination */
  FATAL = 'fatal',
}

/**
 * Log entry structure containing all metadata for a log event
 *
 * @remarks
 * This structure is used internally by the logger and sent to Sentry
 * for error tracking and debugging.
 */
export interface LogEntry {
  level: LogLevel;
  message: string;
  timestamp: Date;
  context?: Record<string, unknown>;
  error?: Error;
  userId?: string;
  sessionId?: string;
  url?: string;
  userAgent?: string;
}

/**
 * Logger configuration options
 *
 * @remarks
 * Default configuration uses INFO level in production, DEBUG in development.
 * Sentry integration is enabled in production by default.
 */
interface LoggerConfig {
  /** Minimum log level to process. Lower levels are ignored */
  minLevel: LogLevel;
  /** Enable console output (always enabled in development) */
  enableConsole: boolean;
  /** Enable sending logs to Sentry (production only by default) */
  enableSentry: boolean;
  /** Enable breadcrumb tracking for error context */
  enableBreadcrumbs: boolean;
  /** Maximum number of breadcrumbs to retain */
  maxBreadcrumbs: number;
}

const defaultConfig: LoggerConfig = {
  minLevel: import.meta.env.PROD ? LogLevel.INFO : LogLevel.DEBUG,
  enableConsole: true,
  enableSentry:
    import.meta.env.PROD || import.meta.env.VITE_SENTRY_ENABLED === 'true',
  enableBreadcrumbs: true,
  maxBreadcrumbs: 100,
};

let currentConfig: LoggerConfig = { ...defaultConfig };

/**
 * Configure the logger with custom settings
 *
 * @param config - Partial configuration to override defaults
 *
 * @remarks
 * Call this early in your application initialization to customize logging behavior.
 * Only specified properties are overridden; others retain their defaults.
 *
 * @example
 * ```typescript
 * // In main.tsx or app initialization
 * configureLogger({
 *   minLevel: LogLevel.WARN, // Only log warnings and errors
 *   enableSentry: false,      // Disable Sentry in development
 * });
 * ```
 */
export const configureLogger = (config: Partial<LoggerConfig>): void => {
  currentConfig = { ...currentConfig, ...config };
};

/**
 * Get current logger configuration
 *
 * @returns A copy of the current logger configuration
 *
 * @example
 * ```typescript
 * const config = getLoggerConfig();
 * console.log('Current log level:', config.minLevel);
 * ```
 */
export const getLoggerConfig = (): LoggerConfig => ({ ...currentConfig });

/**
 * Check if log level should be logged
 */
const shouldLog = (level: LogLevel): boolean => {
  const levels = [
    LogLevel.DEBUG,
    LogLevel.INFO,
    LogLevel.WARN,
    LogLevel.ERROR,
    LogLevel.FATAL,
  ];
  const minLevelIndex = levels.indexOf(currentConfig.minLevel);
  const currentLevelIndex = levels.indexOf(level);
  return currentLevelIndex >= minLevelIndex;
};

/**
 * Format log entry for console output
 */
const formatForConsole = (entry: LogEntry): string => {
  const { level, message, context, error } = entry;
  const timestamp = entry.timestamp.toISOString();

  let output = `[${timestamp}] [${level.toUpperCase()}] ${message}`;

  if (context && Object.keys(context).length > 0) {
    output += `\nContext: ${JSON.stringify(context, null, 2)}`;
  }

  if (error) {
    output += `\nError: ${error.message}\n${error.stack}`;
  }

  return output;
};

/**
 * Console output with appropriate styling
 */
const logToConsole = (entry: LogEntry): void => {
  if (!currentConfig.enableConsole) return;

  const formatted = formatForConsole(entry);

  switch (entry.level) {
    case LogLevel.DEBUG:
      console.debug(formatted);
      break;
    case LogLevel.INFO:
      console.info(formatted);
      break;
    case LogLevel.WARN:
      console.warn(formatted);
      break;
    case LogLevel.ERROR:
    case LogLevel.FATAL:
      console.error(formatted);
      break;
  }
};

/**
 * Send log to Sentry
 */
const logToSentry = (entry: LogEntry): void => {
  if (!currentConfig.enableSentry) return;

  if (entry.error) {
    // Log errors with full context
    sentryCaptureException(entry.error, {
      level: entry.level,
      message: entry.message,
      ...entry.context,
    });
  } else if (entry.level === LogLevel.ERROR || entry.level === LogLevel.FATAL) {
    // Log error messages
    captureMessage(entry.message, entry.level, entry.context);
  } else if (entry.level === LogLevel.WARN) {
    // Log warnings
    captureMessage(entry.message, 'warning', entry.context);
  }
};

/**
 * Add breadcrumb for debugging
 */
const addLogBreadcrumb = (entry: LogEntry): void => {
  if (!currentConfig.enableBreadcrumbs) return;

  addBreadcrumb({
    type: 'debug',
    level: entry.level as 'debug' | 'info' | 'warning' | 'error' | 'fatal',
    category: 'log',
    message: entry.message,
    _data: entry.context,
    timestamp: entry.timestamp.getTime() / 1000,
  });
};

/**
 * Core logging function
 */
const log = (
  level: LogLevel,
  message: string,
  context?: Record<string, unknown>,
  error?: Error
): void => {
  if (!shouldLog(level)) return;

  const entry: LogEntry = {
    level,
    message,
    timestamp: new Date(),
    context,
    error,
    url: window.location.href,
    userAgent: navigator.userAgent,
  };

  // Output to console
  logToConsole(entry);

  // Add breadcrumb for Sentry
  addLogBreadcrumb(entry);

  // Send to Sentry for warnings and errors
  if (
    level === LogLevel.WARN ||
    level === LogLevel.ERROR ||
    level === LogLevel.FATAL
  ) {
    logToSentry(entry);
  }
};

/**
 * Structured logger with Sentry integration
 *
 * @remarks
 * Provides enterprise-grade logging with automatic Sentry integration.
 * Logs at WARN, ERROR, and FATAL levels are automatically sent to Sentry in production.
 * All logs include breadcrumbs for debugging context.
 *
 * @example
 * ```typescript
 * // Basic logging
 * logger.info('User logged in', { userId: user.id });
 * logger.warn('Rate limit approaching', { remaining: 10 });
 * logger.error('Payment failed', { orderId: order.id, reason: 'insufficient_funds' });
 *
 * // With error object
 * try {
 *   await processPayment();
 * } catch (_error) {
 *   logger.error('Payment processing failed', error as Error, { orderId: order.id });
 * }
 *
 * // Specialized loggers
 * logger.api('POST', '/api/orders', 201, 145); // method, url, status, duration
 * logger.userAction('clicked_checkout', { cartValue: 99.99 });
 * logger.performance('page_load', 1234); // metric name, value in ms
 *
 * // Child logger with preset context
 * const orderLogger = logger.child({ orderId: order.id, userId: user.id });
 * orderLogger.info('Processing started');
 * orderLogger.error('Validation failed');
 * ```
 */
export const logger = {
  /**
   * Log debug-level message
   *
   * @param message - The log message
   * @param context - Additional context data
   *
   * @remarks
   * Only logged in development mode (unless minLevel is DEBUG).
   * Use for detailed debugging information.
   */
  debug: (message: string, context?: Record<string, unknown>): void => {
    log(LogLevel.DEBUG, message, context);
  },

  /**
   * Log informational message
   *
   * @param message - The log message
   * @param context - Additional context data
   *
   * @remarks
   * Use for general application events and milestones.
   */
  info: (message: string, context?: Record<string, unknown>): void => {
    log(LogLevel.INFO, message, context);
  },

  /**
   * Log warning message
   *
   * @param message - The log message
   * @param context - Additional context data
   *
   * @remarks
   * Automatically sent to Sentry. Use for potentially harmful situations
   * that don't prevent the application from functioning.
   */
  warn: (message: string, context?: Record<string, unknown>): void => {
    log(LogLevel.WARN, message, context);
  },

  /**
   * Log error message
   *
   * @param message - The log message
   * @param errorOrContext - Error object or context data
   * @param context - Additional context (if errorOrContext is an Error)
   *
   * @remarks
   * Automatically sent to Sentry with full stack trace if an Error is provided.
   * Use for error events that might still allow the application to continue.
   */
  error: (
    message: string,
    errorOrContext?: Error | Record<string, unknown>,
    context?: Record<string, unknown>
  ): void => {
    if (errorOrContext instanceof Error) {
      log(LogLevel.ERROR, message, context, errorOrContext);
    } else {
      log(LogLevel.ERROR, message, errorOrContext);
    }
  },

  /**
   * Log fatal error message
   *
   * @param message - The log message
   * @param errorOrContext - Error object or context data
   * @param context - Additional context (if errorOrContext is an Error)
   *
   * @remarks
   * Automatically sent to Sentry with highest priority.
   * Use for severe errors that may lead to application termination.
   */
  fatal: (
    message: string,
    errorOrContext?: Error | Record<string, unknown>,
    context?: Record<string, unknown>
  ): void => {
    if (errorOrContext instanceof Error) {
      log(LogLevel.FATAL, message, context, errorOrContext);
    } else {
      log(LogLevel.FATAL, message, errorOrContext);
    }
  },

  /**
   * Log API request/response
   *
   * @param method - HTTP method (GET, POST, etc.)
   * @param url - Request URL
   * @param status - HTTP status code
   * @param duration - Request duration in milliseconds
   * @param context - Additional context data
   *
   * @remarks
   * Automatically logs at WARN level for 4xx/5xx responses, INFO for success.
   * Use in API interceptors for comprehensive request tracking.
   *
   * @example
   * ```typescript
   * logger.api('POST', '/api/orders', 201, 145, { orderId: '123' });
   * logger.api('GET', '/api/users', 500, 2341, { error: 'database_timeout' });
   * ```
   */
  api: (
    method: string,
    url: string,
    status: number,
    duration: number,
    context?: Record<string, unknown>
  ): void => {
    const level = status >= 400 ? LogLevel.WARN : LogLevel.INFO;
    log(level, `API ${method} ${url}`, {
      method,
      url,
      status,
      duration,
      ...context,
    });
  },

  /**
   * Log user action for analytics
   *
   * @param action - Action name/description
   * @param context - Additional context data
   *
   * @remarks
   * Use for tracking important user interactions.
   * Logged at INFO level.
   *
   * @example
   * ```typescript
   * logger.userAction('clicked_checkout', { cartValue: 99.99, itemCount: 3 });
   * logger.userAction('viewed_product', { productId: 'abc123', category: 'electronics' });
   * ```
   */
  userAction: (action: string, context?: Record<string, unknown>): void => {
    log(LogLevel.INFO, `User action: ${action}`, context);
  },

  /**
   * Log performance metric
   *
   * @param metric - Metric name
   * @param value - Metric value in milliseconds
   * @param context - Additional context data
   *
   * @remarks
   * Use for tracking application performance metrics.
   * Automatically includes 'unit: ms' in context.
   *
   * @example
   * ```typescript
   * logger.performance('page_load', 1234);
   * logger.performance('api_response', 145, { endpoint: '/api/orders' });
   * logger.performance('database_query', 89, { query: 'SELECT * FROM orders' });
   * ```
   */
  performance: (
    metric: string,
    value: number,
    context?: Record<string, unknown>
  ): void => {
    log(LogLevel.INFO, `Performance: ${metric}`, {
      metric,
      value,
      unit: 'ms',
      ...context,
    });
  },

  /**
   * Create a child logger with preset context
   *
   * @param presetContext - Context data to automatically include in all logs
   * @returns A new logger instance with preset context
   *
   * @remarks
   * Use child loggers to avoid repeating context data.
   * The preset context is merged with any additional context provided in individual log calls.
   *
   * @example
   * ```typescript
   * // Create a logger for a specific order
   * const orderLogger = logger.child({ orderId: order.id, userId: user.id });
   *
   * orderLogger.info('Processing started');
   * // Logs: "Processing started" with { orderId, userId }
   *
   * orderLogger.error('Validation failed', { field: 'email' });
   * // Logs: "Validation failed" with { orderId, userId, field: 'email' }
   *
   * // Create a logger for a specific component
   * const checkoutLogger = logger.child({ component: 'Checkout', page: '/checkout' });
   * checkoutLogger.userAction('submitted_payment');
   * ```
   */
  child: (presetContext: Record<string, unknown>) => ({
    debug: (message: string, context?: Record<string, unknown>) =>
      logger.debug(message, { ...presetContext, ...context }),
    info: (message: string, context?: Record<string, unknown>) =>
      logger.info(message, { ...presetContext, ...context }),
    warn: (message: string, context?: Record<string, unknown>) =>
      logger.warn(message, { ...presetContext, ...context }),
    error: (
      message: string,
      errorOrContext?: Error | Record<string, unknown>,
      context?: Record<string, unknown>
    ) =>
      logger.error(message, errorOrContext, { ...presetContext, ...context }),
    fatal: (
      message: string,
      errorOrContext?: Error | Record<string, unknown>,
      context?: Record<string, unknown>
    ) =>
      logger.fatal(message, errorOrContext, { ...presetContext, ...context }),
  }),
};

/**
 * Initialize logger with user and session context
 *
 * @param userId - Optional user ID to track logs by user
 * @param sessionId - Optional session ID for tracking user sessions
 *
 * @remarks
 * Call this after authentication to associate logs with specific users and sessions.
 * This is helpful for debugging user-specific issues.
 *
 * @example
 * ```typescript
 * // After successful login
 * initLogger(user.id, sessionStorage.getItem('sessionId'));
 *
 * // Or during app initialization
 * initLogger(undefined, crypto.randomUUID());
 * ```
 */
export const initLogger = (userId?: string, sessionId?: string): void => {
  logger.info('Logger initialized', {
    userId,
    sessionId,
    environment: import.meta.env.MODE,
  });
};

/**
 * Log unhandled errors
 */
if (typeof window !== 'undefined') {
  window.addEventListener('error', (event) => {
    logger.error('Unhandled error', event.error, {
      message: event.message,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    logger.error(
      'Unhandled promise rejection',
      event.reason instanceof Error
        ? event.reason
        : new Error(String(event.reason)),
      {
        reason: String(event.reason),
      }
    );
  });
}

export default logger;
